import React, {useState, useEffect} from 'react';
import styled, {keyframes} from 'styled-components';
import {db} from '../../firebase';
import {ref, get, query, orderByChild, limitToLast} from 'firebase/database';

const MEDALS = ['🥇', '🥈', '🥉'];

export const TriviaLeaderboard = ({onBack}) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const snap = await get(query(ref(db, 'trivia/leaderboard'), orderByChild('score'), limitToLast(20)));
                const raw = snap.val() || {};
                const arr = Object.entries(raw)
                    .map(([k, v]) => ({id: k, ...v}))
                    .sort((a, b) => b.score - a.score);
                setData(arr);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    return (
        <Wrapper>
            <TopBar>
                <BackBtn onClick={onBack}>→ חזרה</BackBtn>
                <PageTitle>🏆 טבלת האלופים</PageTitle>
            </TopBar>

            {loading ? (
                <LoadWrap><Spinner/></LoadWrap>
            ) : data.length === 0 ? (
                <EmptyWrap>
                    <EmptyEmoji>📊</EmptyEmoji>
                    <EmptyText>אין עדיין תוצאות</EmptyText>
                    <EmptySub>שחקו במשחק הטריוויה כדי להיכנס לטבלה!</EmptySub>
                </EmptyWrap>
            ) : (
                <Board>
                    <HeaderRow>
                        <ColRank>#</ColRank>
                        <ColName>שם</ColName>
                        <ColScore>ניקוד</ColScore>
                        <ColQ>שאלות</ColQ>
                    </HeaderRow>
                    {data.map((entry, i) => (
                        <Row key={entry.id} $delay={i * 0.04} $isTop={i < 3}>
                            <ColRank>{MEDALS[i] || i + 1}</ColRank>
                            <ColName $bright={i < 3}>{entry.name}</ColName>
                            <ColScore $bright={i < 3}>{entry.score}</ColScore>
                            <ColQ>{entry.totalQuestions || '-'}</ColQ>
                        </Row>
                    ))}
                </Board>
            )}
        </Wrapper>
    );
};

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const rowSlide = keyframes`
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 560px;
    padding: 20px 16px 80px;
    animation: ${slideIn} 0.4s ease-out;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const BackBtn = styled.button`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 14px;
    padding: 8px 16px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
    &:hover { background: rgba(255, 255, 255, 0.15); }
`;

const PageTitle = styled.h2`
    color: #f1f5f9;
    font-size: 22px;
    font-weight: 700;
    margin: 0;
`;

const Board = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    overflow: hidden;
    backdrop-filter: blur(8px);
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 18px;
    background: rgba(139, 92, 246, 0.15);
    border-bottom: 1px solid rgba(139, 92, 246, 0.2);
    font-size: 13px;
    font-weight: 700;
    color: rgba(241, 245, 249, 0.6);
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    animation: ${rowSlide} 0.3s ease ${p => p.$delay}s both;
    transition: background 0.15s;
    
    ${p => p.$isTop && `background: rgba(245, 158, 11, 0.05);`}
    
    &:hover { background: rgba(255, 255, 255, 0.04); }
    &:last-child { border: none; }
`;

const ColRank = styled.div`
    width: 40px;
    flex-shrink: 0;
    text-align: center;
    font-size: 16px;
    color: rgba(241, 245, 249, 0.5);
`;

const ColName = styled.div`
    flex: 1;
    font-size: 16px;
    font-weight: ${p => p.$bright ? 700 : 500};
    color: ${p => p.$bright ? '#fbbf24' : '#f1f5f9'};
`;

const ColScore = styled.div`
    width: 60px;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    color: ${p => p.$bright ? '#f59e0b' : 'rgba(241, 245, 249, 0.8)'};
`;

const ColQ = styled.div`
    width: 50px;
    text-align: center;
    font-size: 14px;
    color: rgba(241, 245, 249, 0.4);
`;

const LoadWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 80px 0;
`;

const Spinner = styled.div`
    width: 40px;
    height: 40px;
    border: 4px solid rgba(139, 92, 246, 0.2);
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

const EmptyWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 20px;
`;

const EmptyEmoji = styled.div`
    font-size: 48px;
`;

const EmptyText = styled.div`
    color: #f1f5f9;
    font-size: 20px;
    font-weight: 700;
`;

const EmptySub = styled.div`
    color: rgba(241, 245, 249, 0.5);
    font-size: 15px;
    text-align: center;
`;
